import { computed, inject, Injectable, Signal, signal } from '@angular/core';
import { Subject, type Observable } from 'rxjs';

import { APP_CONFIG } from '../config/app-config.model';
import { SessionService } from '../session/session.service';
import type { AppRole, Session } from '../session/session.model';
import {
  deriveSession,
  issuerClaimNames,
  type IssuerCategory,
  type SessionDerivation,
} from './claims-mapping';

const UNAUTHENTICATED: Session = {
  isAuthenticated: false,
  roles: [],
  clientCode: null,
};

/**
 * The real session (AITH-43, R-102): derives the `Session` signal exclusively
 * from the validated claims of the active OIDC configuration's authentication
 * result. The issuer category comes from the configuration id — never from a
 * claim value (clarification C1).
 *
 * This service is deliberately library-free: AuthFlowService (which owns the
 * OIDC library boundary) feeds it validated claims and drives the state
 * transitions of data-model.md §4. Derivation is fail-closed and re-runs only
 * when renewal supplies a fresh validated ID token.
 */
@Injectable({ providedIn: 'root' })
export class OidcSessionService extends SessionService {
  private readonly config = inject(APP_CONFIG);
  private readonly state = signal<Session>(UNAUTHENTICATED);
  private readonly category = signal<IssuerCategory | null>(null);
  private readonly epoch = signal(0);
  private readonly renewals = signal(0);

  readonly session: Signal<Session> = this.state.asReadonly();
  readonly isAuthenticated: Signal<boolean> = computed(() => this.session().isAuthenticated);

  /**
   * Ownership lineage of the session — the `SessionService` seam member, for
   * consumers that must detect a change of owner across an await (token
   * provider, contract §3) or must not let one owner's state cross into the
   * next (the filter services, FR-019): advanced by every sign-in derivation
   * and by endSession, but NOT by a renewal derivation —
   * a renewal refreshes the same owner's identity (R-105), so work started
   * under that owner may follow it, while a same-category replacement
   * (sign-out plus new sign-in) must be rejected. Object identity of
   * `session()` cannot make this distinction because renewal also replaces
   * the Session object.
   */
  readonly sessionEpoch: Signal<number> = this.epoch.asReadonly();

  /**
   * Count of successfully applied renewal derivations. Together with
   * `sessionEpoch` this lets the token provider settle a followed renewal
   * only once its validated claims have actually been applied (contract §3):
   * a library-level renewal result alone proves nothing — the derivation may
   * still fail closed. Advances only on a `'renewal'`-cause derivation that
   * produced a session.
   */
  readonly renewalRevision: Signal<number> = this.renewals.asReadonly();

  private readonly changes = new Subject<void>();

  /**
   * Emits after every ownership-relevant mutation (a derivation applied or
   * the session ended), once the signals above reflect it. Lets awaiting
   * consumers (the token provider's renewal hold) react to a change without
   * depending on Angular effect scheduling.
   */
  readonly ownershipChanges$: Observable<void> = this.changes.asObservable();

  /**
   * The configuration category of the current session ('staff' | 'client'),
   * null when unauthenticated. Not part of the SessionService seam — consumed
   * by the auth module (telemetry categorization, token provider).
   */
  readonly issuerCategory: Signal<IssuerCategory | null> = this.category.asReadonly();

  hasAnyRole(roles: readonly AppRole[]): boolean {
    return roles.some((role) => this.session().roles.includes(role));
  }

  /**
   * Applies a validated authentication result. Returns the total derivation
   * outcome; on `notProvisioned` the local session is ended (the caller —
   * AuthFlowService — additionally ends the provider session, clears the
   * configuration's storage, and records the denial event, FR-012).
   */
  applyValidatedClaims(
    configId: IssuerCategory,
    claims: Record<string, unknown>,
    cause: 'signIn' | 'renewal' = 'signIn',
  ): SessionDerivation {
    const names = issuerClaimNames(this.config.auth, configId);
    const derivation = names === null
      ? ({ kind: 'notProvisioned' } as const)
      : deriveSession({ configId, claims }, names);

    if (derivation.kind === 'session') {
      if (cause !== 'renewal') this.epoch.update((value) => value + 1);
      else this.renewals.update((value) => value + 1);
      this.state.set(derivation.session);
      this.category.set(configId);
      this.changes.next();
    } else {
      this.endSession();
    }
    return derivation;
  }

  /** Ends the local session (explicit sign-out, terminal renewal failure, not-provisioned). */
  endSession(): void {
    this.epoch.update((value) => value + 1);
    this.state.set(UNAUTHENTICATED);
    this.category.set(null);
    this.changes.next();
  }
}
